import type { Pick } from './types';

export function money(n: number | null | undefined, digits = 0): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

/** Money with an explicit + on gains, for profit and loss figures. */
export function signedMoney(n: number | null | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const body = `$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
  if (n > 0) return `+${body}`;
  if (n < 0) return `-${body}`;
  return body;
}

export function pct(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

export function signedPct(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

export function signed(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(digits)}`;
}

/** American odds always carry a sign. */
export function odds(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n > 0 ? `+${Math.round(n)}` : `${Math.round(n)}`;
}

/** A spread shows its sign; -3.5 and +3.5 are different bets. */
export function line(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n === 0) return 'PK';
  return `${n > 0 ? '+' : ''}${n}`;
}

export function decimalOdds(american: number): number {
  return american > 0 ? 1 + american / 100 : 1 + 100 / Math.abs(american);
}

export function formatOdds(american: number | null | undefined, format: 'american' | 'decimal'): string {
  if (american == null || !Number.isFinite(american)) return '—';
  return format === 'decimal' ? decimalOdds(american).toFixed(2) : odds(american);
}

const KICKOFF = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function kickoff(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : KICKOFF.format(d);
}

export function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "3m ago", "5h ago" — how bettors actually read a news feed. */
export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return shortDate(iso);
}

export function classForValue(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n === 0) return '';
  return n > 0 ? 'pos' : 'neg';
}

export function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The QB the model priced for a side (its most likely starter), else ESPN's passing leader. */
export function startingQb(pick: Pick, side: 'home' | 'away'): string | null {
  return pick.qb?.[side].starters[0]?.name ?? pick[side].starterQbName;
}
